import { logEvent } from 'firebase/analytics';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { analytics } from '../../firebase';
import { useBoolean } from '../utils/use-boolean';
import { useErrorContext } from '../../components/error';
import { useAuthContext } from '../../auth/auth-provider';
import { ResourceRecommendations, PaginatedResources } from '../../services/learning-api';
import { BaseResourceTableData } from '../../components/shared/resource-table-types';
import { PutUserResourceInteractionBody } from '../../services/learning-api/schema';
import {
  usePostHideRecommendation,
  usePutUserResourceInteraction,
} from '../query/learning-api/use-resource';
import { ProgressStatus, ProgressStatusType } from '../../const/progress';

export interface UseResourceTableProps {
  resources: PaginatedResources[] | ResourceRecommendations[];
  source?: string;
  enableRatingSystem?: boolean;
  getCategoryColor: (title: string) => string;
}

export const useResourceTable = ({
  resources,
  source,
  enableRatingSystem = false,
}: UseResourceTableProps) => {
  const { userId } = useAuthContext();
  const { setError } = useErrorContext();

  // Modal states
  const resourceTypeModal = useBoolean(false);
  const resourceLocationModal = useBoolean(false);
  const progressModal = useBoolean(false);
  const likesModal = useBoolean(false);
  const hideRecommendationModal = useBoolean(false);

  // Component states
  const [evaluateResourceModalOpen, setEvaluateResourceModalOpen] = useState(false);
  const [resourceToEvaluate, setResourceToEvaluate] = useState<string | null>(null);
  const [selectedExternalResource, setSelectedExternalResource] = useState<{
    data: BaseResourceTableData;
    url: string;
  } | null>(null);

  const { putUserResourceInteraction } = usePutUserResourceInteraction({
    onError: (error) => {
      console.error('Error during saving the interaction:', error.message);
      setError({
        code: 500,
        message: 'Failed user interaction',
      });
    },
  });

  const [progress, setProgress] = useState<{ [id: string]: ProgressStatusType }>({});

  // Progress options
  const progressOptions = [
    { label: 'Not started', value: ProgressStatus.NotStarted },
    { label: 'Started', value: ProgressStatus.InProgress },
    { label: 'Completed', value: ProgressStatus.Completed },
  ];

  // Update progress state
  const updateProgressState = useCallback(() => {
    const newProgress: { [id: string]: ProgressStatusType } = {};

    for (const resource of resources) {
      newProgress[resource.id] = resource.userProgressStatus ?? ProgressStatus.NotStarted;
    }

    setProgress((prev) => {
      const updated: { [id: string]: ProgressStatusType } = { ...prev, ...newProgress };

      const validIds = new Set(resources.map((res) => res.id));
      for (const id of Object.keys(updated)) {
        // need to keep resourceToEvaluate's progress state tracked even when it's not displayed on the current table page, due to an edge case where resourceTable is sorted by progress ->
        // user sets resource to completed, moving it off the current page due to sorting -> user likes it via the evaluation modal -> progress state of that resource is written to BE as part of like's putUserInteraction
        if (!validIds.has(id) && id !== resourceToEvaluate) {
          delete updated[id];
        }
      }

      return updated;
    });
  }, [resources]);

  useEffect(() => {
    updateProgressState();
  }, [resources, updateProgressState]);

  // Event handlers
  const handleLikeClick = useCallback(
    async (id: string) => {
      const resource = resources.find((r) => r.id === id);
      const currentStatus = progress[id] || ProgressStatus.NotStarted;
      const newLikeValue = !resource?.isLikedByUser;

      try {
        await putUserResourceInteraction({
          resource_id: id,
          liked: newLikeValue,
          status: currentStatus as PutUserResourceInteractionBody['status'],
        });

        const action = newLikeValue ? 'add_resource_likes' : 'remove_resource_likes';
        logEvent(analytics, action, {
          item_id: id,
          total_likes: (resource?.totalLikes ?? 0) + (newLikeValue ? 1 : -1),
          custom_user_id: userId,
        });
      } catch (error: any) {
        console.error('Error during interaction:', error.message);
      }
    },
    [resources, progress, putUserResourceInteraction, userId]
  );

  const handleProgressChange = useCallback(
    async (id: string, value: ProgressStatusType) => {
      setProgress((prev) => ({ ...prev, [id]: value }));

      const resource = resources.find((r) => r.id === id);
      const likedNow = !!resource?.isLikedByUser;

      try {
        await putUserResourceInteraction({
          resource_id: id,
          liked: likedNow,
          status: value as ProgressStatusType,
        });

        if (value === ProgressStatus.Completed && enableRatingSystem && !likedNow) {
          setResourceToEvaluate(id);
          setEvaluateResourceModalOpen(true);
        }
        logEvent(analytics, 'change_resource_status', {
          item_id: id,
          progress_status: value,
          custom_user_id: userId,
        });
      } catch (error: any) {
        console.error('Error updating progress status:', error.message);
      }
    },
    [putUserResourceInteraction, enableRatingSystem, userId, resources]
  );

  const handleViewClick = useCallback(
    (data: BaseResourceTableData) => {
      if (!data.isInternal && data.personalLogin) {
        setSelectedExternalResource({ data, url: data.url });
        return;
      }

      logEvent(analytics, 'view_resource_item', {
        item_id: data.id,
        item_name: data.title,
        media_type: data.mediaType,
        location: 'internal',
        custom_user_id: userId,
        viewed_from: source,
      });

      window.open(data.url, '_blank');
    },
    [userId, source]
  );

  const handleExternalContinue = useCallback(() => {
    if (!selectedExternalResource) return;

    const { data, url } = selectedExternalResource;
    logEvent(analytics, 'view_resource_item', {
      item_id: data.id,
      item_name: data.title,
      media_type: data.mediaType,
      location: 'external',
      custom_user_id: userId,
      viewed_from: source,
    });

    window.open(url, '_blank');
    setSelectedExternalResource(null);
  }, [selectedExternalResource, userId, source]);

  const getProgressDropdownClass = useCallback((progressValue: string) => {
    switch (progressValue) {
      case ProgressStatus.Completed:
        return 'progressDropdown progress-completed';
      case ProgressStatus.InProgress:
        return 'progressDropdown progress-inprogress';
      case ProgressStatus.NotStarted:
      default:
        return 'progressDropdown progress-notstarted';
    }
  }, []);

  // Handle hiding of learning resource recommendations
  const { postHideRecommendation } = usePostHideRecommendation();
  const [recommendationToHide, setRecommendationToHide] = useState(' ');

  const confirmHideRecommendation = useCallback(
    async (recommendationToHide: string) => {
      try {
        await postHideRecommendation(recommendationToHide);

        logEvent(analytics, 'hide_recommendation', {
          resource_id: recommendationToHide,
          user_id: userId,
        });
      } catch (err) {
        console.error('Failed to hide learning recommendation', err);
      } finally {
        hideRecommendationModal.onFalse();
      }
    },
    [hideRecommendationModal, postHideRecommendation, userId]
  );

  // Create table data

  const hasRecommendationHidden = (
    resource: PaginatedResources | ResourceRecommendations
  ): resource is ResourceRecommendations => {
    return 'recommendationHidden' in resource;
  };

  const tableData = useMemo(() => {
    return resources
      .filter(
        (resource) => !hasRecommendationHidden(resource) || resource.recommendationHidden === false
      )
      .map((resource: any) => {
        const {
          id,
          title,
          mediaType,
          skillCategory,
          skillLevel,
          url,
          isInternal,
          personalLogin,
          totalLikes,
          isLikedByUser,
          isBookmarked,
          isRoleDeptMatched,
        } = resource;

        return {
          id,
          title,
          mediaType,
          isInternal,
          personalLogin,
          category: skillCategory?.[0]?.title || '',
          skillLevel,
          url,
          liked: !!isLikedByUser,
          isBookmarked,
          likeCount: totalLikes ?? 0,
          userProgressStatus: (progress[id] || ProgressStatus.NotStarted) as ProgressStatus,
          isRoleDeptMatched,
        };
      });
  }, [resources, progress]);

  return {
    // State
    progress,
    selectedExternalResource,
    recommendationToHide,
    evaluateResourceModalOpen,
    resourceToEvaluate,
    progressOptions,
    tableData,

    // Modal states
    resourceTypeModal,
    resourceLocationModal,
    progressModal,
    likesModal,
    hideRecommendationModal,

    // Event handlers
    handleLikeClick,
    handleProgressChange,
    handleViewClick,
    handleExternalContinue,
    getProgressDropdownClass,
    confirmHideRecommendation,

    // Setters
    setSelectedExternalResource,
    setEvaluateResourceModalOpen,
    setResourceToEvaluate,
    setRecommendationToHide,
  };
};
